import React, { use, useEffect, useRef, useState,useMemo } from "react";
import { Play, Pause } from "lucide-react";
import TimelineBar from "./TimelineBar";
import TrackView from "./TrackView";
import { TRANSITIONS } from "../utils/transitions";
import { ANIMATIONS } from "../utils/animations";
import { ANIMATION_DELTAS } from "../utils/animationDeltas";
import { convertAnimationToKeyframes } 
  from "../utils/convertAnimationToKeyframes";

import { useAudioEngine } from "../hooks/useAudioEngine";
import { drawImageFit } from "../utils/canvasUtils";



// testing audio url
//url = https://www.learningcontainer.com/wp-content/uploads/2020/02/Kalimba.mp3

export default function FramePlayer({ images,
   transition,
   durations,
   setDurations,
   transitionDuration,
   clipTransitions,
   setClipTransitions,
   onSelectTransition,
   clipAnimations,
   setClipAnimations,
   clipAnimationTiming,
   setClipAnimationTiming,
   onSelectAnimation,
   animationDuration,
   setAnimationDuration,
   audioTracks,
  keyframes,
  setKeyframes,
onChangeCurrentIndex,
seekTime,
exportTransformsRef ,
editorCanvasSizeRef,
filters}) {

  const canvasRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const playingRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);
  const rafRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({
  width: 180,
  height: 320, // 9:16
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [activeBox, setActiveBox] = useState(null); 
  const draggingRef = useRef(null);
  //const [keyframes, setKeyframes] = useState({});





  const SLIDE_DURATION = 2;     // seconds
  const TRANSITION_DURATION = Number(transitionDuration) || 1;  // seconds (fade)

  const totalDuration = durations.reduce((a, b) => a + b, 0) + (durations.length * TRANSITION_DURATION);

//   const clipAnimations = [
//   "pan-left",   // slide 0 → will pan left       
//   //"pan-right",  // slide 2 → pan right
//   "pan-up",     // slide 3 → pan up
// ];



  // Load images into <img> objects
  const [loaded, setLoaded] = useState([]);
  const audioEngine = useAudioEngine(audioTracks);

// when play/pause changes
  useEffect(() => {
  if (seekTime != null) {
    internalTimeRef.current = seekTime;
    if (audioEngine) audioEngine.seek(seekTime);
    redraw();
  }
}, [seekTime]);

// when images prop changes, reload
  useEffect(() => {
    if (!images) return;
    const arr = [];

    let loadedCount = 0;
    images.forEach((file) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        loadedCount++;
        if (loadedCount === images.length) setLoaded(arr);
      };
      arr.push(img);
    });
  }, [images]);


  // when loaded images change, draw first frame
  useEffect(() => {
  // stop old loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  setPlaying(false);
  setCurrentTime(0);

  // draw the first frame once images load
  if (loaded.length) drawFrame(0);
}, [loaded]);


// when transitionDuration changes, reset
useEffect(() => {
  // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset internal clock
  lastTimestampRef.current = 0;

  // Reset slideshow timing
  setCurrentTime(0);

  // Redraw first frame with new duration
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [transitionDuration]);

// when indivisual duration changes, reset
useEffect(() => {
  console.log("Durations changed:", durations);
  // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset internal clock
  lastTimestampRef.current = 0;

  // Reset slideshow timing
  setCurrentTime(0);

  // Redraw first frame with new duration
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [durations]);


// when transition type changes, reset
useEffect(() => {
  // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset playback clock
  lastTimestampRef.current = 0;

  // Reset position
  setCurrentTime(0);

  // Draw first frame with the new transition
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [transition]);

// when images change, set default durations
useEffect(() => {
  if (!images) return;

  // Rebuild durations array so it matches the number of images
  setDurations((prev) => {
    const newDurations = [];

    for (let i = 0; i < images.length; i++) {
      newDurations[i] = prev[i] || 2; // old duration if exists, else default
    }

    return newDurations;
  });
}, [images]);

//Whenever images change (or durations change), rebuild transitions array
useEffect(() => {
  if (!images.length) return;

  // N clips → N-1 transitions
  const newTransitions = [];

  for (let i = 0; i < images.length - 1; i++) {
    newTransitions[i] = "fade"; // default
  }

  setClipTransitions(newTransitions);
}, [images]);

//Whenever images change (or durations change), rebuild Animation array
useEffect(() => {
  if (!images.length) return;

  setClipAnimations((prev) => {
    const next = [...prev];

    for (let i = 0; i < images.length; i++) {
      if (next[i] === undefined) {
        next[i] = null;   // ✅ default = no animation
      }
    }

    return next;
  });
}, [images]);

//Whenever images change (or durations change), rebuild Animation Timing array
useEffect(() => {
  if (!images.length) return;

  setClipAnimationTiming((prev) => {
    const next = [...prev];
    for (let i = 0; i < images.length; i++) {
      if (!next[i]) next[i] = "full"; // default
    }
    return next;
  });
}, [images]);

//Whenever images change (or durations change), rebuild Animation Duration array
useEffect(() => {
  if (!images.length) return;

  setAnimationDuration((prev) => {
    const next = [...prev];
    for (let i = 0; i < images.length; i++) {
      if (next[i] === undefined) {
        next[i] = 1.0; // ✅ default 1s
      }
    }
    return next;
  });
}, [images]);



useEffect(() => {
  console.log("Clip transitions updated:" , clipTransitions);
    // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset playback clock
  lastTimestampRef.current = 0;

  // Reset position
  setCurrentTime(0);

  // Draw first frame with the new transition
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [clipTransitions]);

useEffect(() => {
  console.log("Clip animation updated:" , clipAnimations);
    // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset playback clock
  lastTimestampRef.current = 0;

  // Reset position
  setCurrentTime(0);

  // Draw first frame with the new transition
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [clipAnimations]);


useEffect(() => {
  console.log("Clip animation duration updated:" , animationDuration);
    // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset playback clock
  lastTimestampRef.current = 0;

  // Reset position
  setCurrentTime(0);

  // Draw first frame with the new transition
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [animationDuration]);


useEffect(() => {
  console.log("Clip animation timing updated:" , clipAnimationTiming);
    // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset playback clock
  lastTimestampRef.current = 0;

  // Reset position
  setCurrentTime(0);

  // Draw first frame with the new transition
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [clipAnimationTiming]);


useEffect(() => {
  console.log("Clip filter  updated:" , filters);
    // Stop animation loop
  playingRef.current = false;
  cancelAnimationFrame(rafRef.current);

  // Reset playback clock
  lastTimestampRef.current = 0;

  // Reset position
  setCurrentTime(0);

  // Draw first frame with the new transition
  if (loaded.length) drawFrame(0);

  setPlaying(false);
}, [filters]);



const cursorMap = {
  tl: "nwse-resize",
  br: "nwse-resize",

  tr: "nesw-resize",
  bl: "nesw-resize",

  tm: "ns-resize",
  bm: "ns-resize",

  ml: "ew-resize",
  mr: "ew-resize",
};


function drawHandles(ctx, box) {
  const { dx, dy, drawW, drawH } = box;

  const visual = 12;   // visible square size
  const half = visual / 2;

  ctx.save();

  // ✅ Outer rectangle
  ctx.strokeStyle = "#e8eeefff";
  ctx.lineWidth = 1.5;
  ctx.strokeRect(dx, dy, drawW, drawH);

  // ✅ Inner thirds grid
  ctx.globalAlpha = 0.4;
  ctx.beginPath();
  ctx.moveTo(dx + drawW / 3, dy);
  ctx.lineTo(dx + drawW / 3, dy + drawH);
  ctx.moveTo(dx + (2 * drawW) / 3, dy);
  ctx.lineTo(dx + (2 * drawW) / 3, dy + drawH);
  ctx.moveTo(dx, dy + drawH / 3);
  ctx.lineTo(dx + drawW, dy + drawH / 3);
  ctx.moveTo(dx, dy + (2 * drawH) / 3);
  ctx.lineTo(dx + drawW, dy + (2 * drawH) / 3);
  ctx.stroke();
  ctx.globalAlpha = 1;

  // ✅ 8 visible resize handles
  const handles = [
    [dx, dy],                         // tl
    [dx + drawW / 2, dy],            // tm
    [dx + drawW, dy],                // tr
    [dx, dy + drawH / 2],            // ml
    [dx + drawW, dy + drawH / 2],    // mr
    [dx, dy + drawH],                // bl
    [dx + drawW / 2, dy + drawH],    // bm
    [dx + drawW, dy + drawH],        // br
  ];

  ctx.fillStyle = "#e8eeefff";
  ctx.strokeStyle = "#000";
  ctx.lineWidth = 1;

  handles.forEach(([x, y]) => {
    ctx.fillRect(x - half, y - half, visual, visual);
    ctx.strokeRect(x - half, y - half, visual, visual);
  });

  ctx.restore();
}



function hitTestHandle(x, y, box) {
  const { dx, dy, drawW, drawH } = box;
  const r = 16; // larger radius for easy clicking

  const list = [
    { type: "tl", x: dx, y: dy },
    { type: "tm", x: dx + drawW / 2, y: dy },
    { type: "tr", x: dx + drawW, y: dy },
    { type: "ml", x: dx, y: dy + drawH / 2 },
    { type: "mr", x: dx + drawW, y: dy + drawH / 2 },
    { type: "bl", x: dx, y: dy + drawH },
    { type: "bm", x: dx + drawW / 2, y: dy + drawH },
    { type: "br", x: dx + drawW, y: dy + drawH },
  ];

  return list.find(
    h =>
      Math.abs(x - h.x) <= r &&
      Math.abs(y - h.y) <= r
  );
}

function getScaleDelta(type, dx, dy, box) {
  const { drawW, drawH } = box;

  let sx = 0;
  let sy = 0;

  switch (type) {
    case "tl": sx = -dx / drawW; sy = -dy / drawH; break;
    case "tm": sy = -dy / drawH; break;
    case "tr": sx =  dx / drawW; sy = -dy / drawH; break;

    case "ml": sx = -dx / drawW; break;
    case "mr": sx =  dx / drawW; break;

    case "bl": sx = -dx / drawW; sy =  dy / drawH; break;
    case "bm": sy =  dy / drawH; break;
    case "br": sx =  dx / drawW; sy =  dy / drawH; break;
  }

  // average for diagonal handles
  return sx !== 0 && sy !== 0 ? (sx + sy) / 2 : sx + sy;
}

function applyExponentialScale(startScale, scaleDelta) {
  const MIN_SCALE = 0.2;
  const SENSITIVITY = 1.2; // tweak feel here

  const ratio = Math.exp(scaleDelta * SENSITIVITY);

  return Math.max(MIN_SCALE, startScale * ratio);
}


function onMouseDown(e) {
  const rect = canvasRef.current.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const box = boundsRef.current;
  if (!box) return;

  const handle = hitTestHandle(x, y, box);

  selectedIndexRef.current = box.index;

  draggingRef.current = {
    type: handle?.type || "move",
    startX: x,
    startY: y,
    startScale: slideTransforms[box.index]?.scale ?? 1,
    startOX: slideTransforms[box.index]?.offsetX ?? 0,
    startOY: slideTransforms[box.index]?.offsetY ?? 0,
    index: box.index,
  };

  canvasRef.current.style.cursor = handle
    ? cursorMap[handle.type]
    : "move";

  redraw();
}




function onMouseMove(e) {
  const rect = canvasRef.current.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const box = boundsRef.current;

  // ============================
  // 1️⃣ HANDLE HOVER
  // ============================
  if (!draggingRef.current) {
    if (box) {
      const h = hitTestHandle(x, y, box);

      if (h) {
        canvasRef.current.style.cursor = cursorMap[h.type];
      } else if (
        x >= box.dx &&
        x <= box.dx + box.drawW &&
        y >= box.dy &&
        y <= box.dy + box.drawH
      ) {
        canvasRef.current.style.cursor = "move";
      } else {
        canvasRef.current.style.cursor = "default";
      }
    }
    return;
  }

  // ============================
  // 2️⃣ DRAGGING / RESIZING
  // ============================
  const d = draggingRef.current;
  const dx = x - d.startX;
  const dy = y - d.startY;

  let scale = d.startScale;
  let updatedOffsetX = d.startOX;
  let updatedOffsetY = d.startOY;

  // ----------------------------
  // MOVE
  // ----------------------------
  if (d.type === "move") {
    updatedOffsetX = d.startOX + dx;
    updatedOffsetY = d.startOY + dy;

    updateSlide(d.index, {
      offsetX: updatedOffsetX,
      offsetY: updatedOffsetY,
    });
  }

  // ----------------------------
  // RESIZE
  // ----------------------------
  else {
    const scaleDelta = getScaleDelta(
      d.type,
      dx,
      dy,
      box
    );

    scale = applyExponentialScale(
      d.startScale,
      scaleDelta
    );

    updateSlide(d.index, { scale });
  }

  // ============================
  // 3️⃣ KEYFRAME SYNC
  // ============================
  const slide = timeline[d.index];
  const localTime = currentTime - slide.start;

  syncKeyframeWhileDragging(d.index, localTime, {
    scale,
    ox: updatedOffsetX,
    oy: updatedOffsetY,
  });

  redraw();
}

function fitSlideVertically(index) {
  console.log("Fitting slide", index, "vertically");
  // ✅ FIXED: Use loaded array instead of timeline.image
  if (index == null || !loaded[index]) return;

  const canvas = canvasRef.current;
  if (!canvas) return;

  const img = loaded[index];  // ✅ Get image from loaded array
  const canvasW = canvas.width;
  const canvasH = canvas.height;

  const imgW = img.width;
  const imgH = img.height;

  if (!imgW || !imgH) return;

  // 1️⃣ scale to fit height
  const scale = canvasH / imgH;

  // 2️⃣ center image
  const offsetX = (canvasW - imgW * scale) / 2;
  const offsetY = (canvasH - imgH * scale) / 2;

  updateSlide(index, {
    scale,
    offsetX,
    offsetY,
  });

  // ✅ optional: sync keyframe at current time
  const slide = timeline[index];
  if (slide) {
    const localTime = currentTime - slide.start;
    syncKeyframeWhileDragging(index, localTime, {
      scale,
      ox: offsetX,
      oy: offsetY,
    });
  }

  redraw();
}


function syncKeyframeWhileDragging(index, localTime, patch) {
  setKeyframes(prev => {
    const list = prev[index];
    if (!list) return prev;

    const i = list.findIndex(k => Math.abs(k.time - localTime) < 0.05);
    if (i < 0) return prev;

    const updated = [...list];
    updated[i] = { ...updated[i], ...patch };

    return { ...prev, [index]: updated };
  });
}




function onMouseUp() {
  const d = draggingRef.current;
  draggingRef.current = null;

  if (d) {
    const slide = timeline[d.index];
    const localTime = currentTime - slide.start;

    const t = slideTransforms[d.index] || {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
    };

    // ✅ COMMIT FINAL POSE AS A KEYFRAME
    setKeyframes(prev => {
      const list = prev[d.index] ? [...prev[d.index]] : [];

      const frame = {
        time: localTime,
        scale: t.scale ?? 1,
        ox: t.offsetX ?? 0,
        oy: t.offsetY ?? 0,
      };

      const idx = list.findIndex(k => Math.abs(k.time - localTime) < 0.05);
      if (idx >= 0) list[idx] = frame;
      else list.push(frame);

      list.sort((a, b) => a.time - b.time);

      return { ...prev, [d.index]: list };
    });
  }

  redraw(); // ✅ now interpolation immediately uses this pose
}



const updateSlide = (index, patch) => {
  setSlideTransforms(prev => {
    const copy = [...prev];
    copy[index] = { ...copy[index], ...patch };
    return copy;
  });
};



const [slideTransforms, setSlideTransforms] = useState([]);


// Initialize slideTransforms when loaded images change
useEffect(() => {
  if (loaded.length) {
    setSlideTransforms(
      loaded.map(() => ({
        scale: 1,
        offsetX: 0,
        offsetY: 0,
      }))
    );
  }
}, [loaded.length]);


const computeTimeline = () => {
  const timeline = [];
  let sum = 0;

  for (let i = 0; i < durations.length; i++) {
    const slideDuration = durations[i];
    const transitionType = clipTransitions[i] || "fade"; // last clip has no transition

    let segmentLength = slideDuration;

    // Add transition only if not last slide
    if (i < durations.length - 1) {
      segmentLength += transitionDuration;
    }

    timeline.push({
      index: i,
      start: sum,
      end: sum + segmentLength,
      slideDuration,
      transitionType,
      // Animation
      animationType: clipAnimations?.[i] || null,
      animationTiming: clipAnimationTiming?.[i] || "full",
      animationDuration: animationDuration?.[i] || 1.0 // seconds
    });

    sum += segmentLength;
  }

  return timeline;
};


const timeline = useMemo(() => computeTimeline(), [durations, transitionDuration, clipTransitions, clipAnimations,clipAnimationTiming,animationDuration]);

const findSlideAtTime = (t) => {
  for (let i = 0; i < timeline.length; i++) {
    if (t >= timeline[i].start && t < timeline[i].end) {
      return timeline[i];
    }
  }
  return timeline[timeline.length - 1];
};

const selectedIndexRef = useRef(null);
const boundsRef = useRef(null);


const redraw = () => {
  if (!canvasRef.current) return;
  drawFrame(currentTime);
};

useEffect(() => {
  function handleOutsideClick(e) {
    if (!canvasRef.current) return;

    // ✅ If click is OUTSIDE canvas → deselect
    if (!canvasRef.current.contains(e.target)) {
      selectedIndexRef.current = null;
      draggingRef.current = null;
      redraw();   // ✅ remove grid immediately
    }
  }

  document.addEventListener("mousedown", handleOutsideClick);
  return () => {
    document.removeEventListener("mousedown", handleOutsideClick);
  };
}, []);


const lerp = (a, b, p) => a + (b - a) * p;

function getInterpolatedTransform(slideIndex, localTime) {
  const frames = keyframes[slideIndex];

  // ✅ No keyframes → fallback to live transform
  if (!frames || frames.length === 0) {
    const t = slideTransforms[slideIndex] || {};
    return {
      scale: t.scale ?? 1,
      offsetX: t.offsetX ?? 0,
      offsetY: t.offsetY ?? 0,
    };
  }

  // ✅ Before first keyframe
  if (localTime <= frames[0].time) {
    const k = frames[0];
    return { scale: k.scale, offsetX: k.ox, offsetY: k.oy };
  }

  // ✅ After last keyframe
  if (localTime >= frames[frames.length - 1].time) {
    const k = frames[frames.length - 1];
    return { scale: k.scale, offsetX: k.ox, offsetY: k.oy };
  }

  // ✅ Between two keyframes
  for (let i = 0; i < frames.length - 1; i++) {
    const a = frames[i];
    const b = frames[i + 1];

    if (localTime >= a.time && localTime <= b.time) {
      const p = (localTime - a.time) / (b.time - a.time);

      return {
        scale:   lerp(a.scale, b.scale, p),
        offsetX: lerp(a.ox,    b.ox,    p),
        offsetY: lerp(a.oy,    b.oy,    p),
      };
    }
  }
}

// Add a keyframe for the current slide at the current time
function addKeyframeForCurrentSlide() {
  const slide = timeline[currentIndex];
  if (!slide) return;
  console.log("Keyframes:", keyframes);


  const localTime = currentTime - slide.start;

  const t = slideTransforms[currentIndex] || {
    scale: 1,
    offsetX: 0,
    offsetY: 0,
  };

  setKeyframes(prev => {
    const list = prev[currentIndex] ? [...prev[currentIndex]] : [];

    const frame = {
      time: localTime,
      scale: t.scale ?? 1,
      ox: t.offsetX ?? 0,
      oy: t.offsetY ?? 0,
    };

    const idx = list.findIndex(k => Math.abs(k.time - localTime) < 0.05);
    if (idx >= 0) list[idx] = frame;
    else list.push(frame);

    list.sort((a, b) => a.time - b.time);

    return { ...prev, [currentIndex]: list };
  });
}


// draw a frame at time t (seconds)
// draw a frame at time t (seconds)
// const drawFrame = (t) => {
//   if (!loaded.length || !timeline.length) return;

//   const canvas = canvasRef.current;
//   const ctx = canvas.getContext("2d");
//   ctx.clearRect(0, 0, canvas.width, canvas.height);

//   const slide = findSlideAtTime(t);
//   const local = t - slide.start;
//   const isTransition = local >= slide.slideDuration;

//   setCurrentIndex(slide.index);
//   const index = slide.index;   // keep for local use

//   const nextIndex = (index + 1) % loaded.length;

//   const imgA = loaded[index];
//   const imgB = loaded[nextIndex];

//   // const scale = slide.scale ?? 1;
//   // const ox = slide.offsetX ?? 0;
//   // const oy = slide.offsetY ?? 0;
//   const transform = slideTransforms[index] || {};
//   const scale = transform.scale ?? 1;
//   const ox = transform.offsetX ?? 0;
//   const oy = transform.offsetY ?? 0;


//   // ---------- NORMAL SLIDE ----------
//   if (!isTransition) {
//     const pFull = local / slide.slideDuration;

//     const animType = slide.animationType;
//     const timing = slide.animationTiming;
//     const animDuration = slide.animationDuration;

//     let shouldAnimate = false;
//     let p = pFull;

//     if (timing === "start") {
//       const w = animDuration / slide.slideDuration;
//       if (pFull <= w) {
//         shouldAnimate = true;
//         p = pFull / w;
//       }
//     }
//     else if (timing === "end") {
//       const w = animDuration / slide.slideDuration;
//       if (pFull >= 1 - w) {
//         shouldAnimate = true;
//         p = (pFull - (1 - w)) / w;
//       }
//     }
//     else if (timing === "both") {
//       const w = animDuration / slide.slideDuration;
//       if (pFull <= w) {
//         shouldAnimate = true;
//         p = pFull / w;
//       }
//       else if (pFull >= 1 - w) {
//         shouldAnimate = true;
//         p = (pFull - (1 - w)) / w;
//       }
//     }
//     else {
//       shouldAnimate = true;
//       p = pFull;
//     }

//     // ✅ ANIMATED DRAW
//     if (shouldAnimate && animType && ANIMATIONS[animType]) {
//       ANIMATIONS[animType](
//         ctx,
//         imgA,
//         Math.min(1, Math.max(0, p)),
//         canvas,
//         scale,
//         ox,
//         oy
//       );
//     }
//     // ✅ STATIC DRAW (ASPECT SAFE)
//     else {
//       const bounds = drawImageFit(ctx, imgA, canvas, scale, ox, oy);
//       // ✅ Persist bounds without re-render
//       boundsRef.current = { ...bounds, index };

//       // ✅ If this slide is selected, draw grid
//       if (selectedIndexRef.current === index) {
//         drawHandles(ctx, boundsRef.current);
//       }

//     }

//     return;
//   }

//   // ---------- TRANSITION PHASE ----------
//   const p = (local - slide.slideDuration) / transitionDuration;

//   const transitionType = slide.transitionType;
//   const transitionFn =
//     TRANSITIONS[transitionType] || TRANSITIONS["fade"];

//   const nextSlide = timeline[index + 1];
//   //const scaleB = nextSlide?.scale ?? 1;

//   const nextTransform = slideTransforms[nextIndex] || {};

//   const scaleB = nextTransform.scale ?? 1;
//   const oxB = nextTransform.offsetX ?? 0;
//   const oyB = nextTransform.offsetY ?? 0;
//   //console.log("values in FramePlayer:", p,canvas,scale,ox, oy,oxB,oyB);

//   transitionFn(
//     ctx,
//     imgA,
//     imgB,
//     p,
//     canvas,
//     scale,
//     scaleB,
//     ox,
//     oy,
//     oxB,
//     oyB
//   );
//   // After transitionFn(...)
// // ✅ Always draw grid for selected slide (play or pause)
// if (
//   boundsRef.current &&
//   selectedIndexRef.current === boundsRef.current.index
// ) {
//   drawHandles(ctx, boundsRef.current);
// }


// };

// ✅ KEYFRAME-DRIVEN drawFrame
// const drawFrame = (t) => {
//   if (!loaded.length || !timeline.length) return;

//   const canvas = canvasRef.current;
//   const ctx = canvas.getContext("2d");
//   ctx.clearRect(0, 0, canvas.width, canvas.height);

//   const slide = findSlideAtTime(t);
//   const local = t - slide.start;
//   const isTransition = local >= slide.slideDuration;

//   setCurrentIndex(slide.index);
//   if (onChangeCurrentIndex) {
//       onChangeCurrentIndex(slide.index);
//   }

//   const index = slide.index;
//   const nextIndex = (index + 1) % loaded.length;

//   const imgA = loaded[index];
//   const imgB = loaded[nextIndex];

//   // ✅ ✅ KEYFRAME TRANSFORM (THIS IS THE CORE CHANGE)
//   const live = slideTransforms[index] || {};

// const animated = draggingRef.current?.index === index
//   ? {
//       scale: live.scale ?? 1,
//       offsetX: live.offsetX ?? 0,
//       offsetY: live.offsetY ?? 0,
//     }                            // ✅ LIVE DRAG OVERRIDES KEYFRAMES
//   : getInterpolatedTransform(index, local);   // ✅ Normal playback mode

//   const scale = animated.scale;
//   const ox = animated.offsetX;
//   const oy = animated.offsetY;

//   // ---------- NORMAL SLIDE ----------
//   if (!isTransition) {
//     const bounds = drawImageFit(ctx, imgA, canvas, scale, ox, oy);
//     boundsRef.current = { ...bounds, index };

//     // ✅ Draw resize grid when selected
//     if (selectedIndexRef.current === index) {
//       drawHandles(ctx, boundsRef.current);
//     }

//     return;
//   }

//   // ---------- TRANSITION PHASE ----------
//   const p = (local - slide.slideDuration) / transitionDuration;

//   const transitionType = slide.transitionType;
//   const transitionFn = TRANSITIONS[transitionType] || TRANSITIONS["fade"];

//   const nextTransform = getInterpolatedTransform(nextIndex, 0);
//   const scaleB = nextTransform.scale;
//   const oxB = nextTransform.offsetX;
//   const oyB = nextTransform.offsetY;

//   transitionFn(
//     ctx,
//     imgA,
//     imgB,
//     p,
//     canvas,
//     scale,
//     scaleB,
//     ox,
//     oy,
//     oxB,
//     oyB
//   );

//   // ✅ Grid still visible during transition
//   if (
//     boundsRef.current &&
//     selectedIndexRef.current === boundsRef.current.index
//   ) {
//     drawHandles(ctx, boundsRef.current);
//   }
// };
// ✅ FULL drawFrame (Keyframes + Hard Animations + Transitions)

function getCanvasFilter(type) {
  switch (type) {
    case "grayscale":
      return "grayscale(1)";
    case "sepia":
      return "sepia(1)";
    case "contrast":
      return "contrast(1.4)";
    case "warm":
      return "brightness(1.05) saturate(1.3)";
    case "cool":
      return "brightness(0.95) saturate(0.9) hue-rotate(180deg)";
    case "vintage":
      return "sepia(0.6) contrast(1.2) saturate(0.8)";
    default:
      return "none";
  }
}

const drawFrame = (t) => {
  if (!loaded.length || !timeline.length) return;


  const canvas = canvasRef.current;
    editorCanvasSizeRef.current = {
    width: canvas.width,
    height: canvas.height
  };

  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const slide = findSlideAtTime(t);
  const local = t - slide.start;
  const isTransition = local >= slide.slideDuration;

  const index = slide.index;
  const nextIndex = (index + 1) % loaded.length;

  setCurrentIndex(index);
  onChangeCurrentIndex?.(index);

  const imgA = loaded[index];
  const imgB = loaded[nextIndex];

  // -----------------------------
  // Helpers
  // -----------------------------
  const hasKeyframes =
    Array.isArray(keyframes?.[index]) &&
    keyframes[index].length > 0;

  const live = slideTransforms[index] || {};

  // -----------------------------
  // ---------- NORMAL SLIDE ------
  // -----------------------------
  if (!isTransition) {
    const pFull = local / slide.slideDuration;

    let scale, ox, oy;

    // 🥇 1. LIVE DRAG (highest priority)
    if (draggingRef.current?.index === index) {
      scale = live.scale ?? 1;
      ox = live.offsetX ?? 0;
      oy = live.offsetY ?? 0;
    }

    // 🥈 2. KEYFRAMES (disable hard animation)
    else if (hasKeyframes) {
      const kf = getInterpolatedTransform(index, local);
      scale = kf.scale ?? 1;
      ox = kf.offsetX ?? 0;
      oy = kf.offsetY ?? 0;
    }

    // 🥉 3. HARD-CODED ANIMATION (fallback only)
    else {
      const base = slideTransforms[index] || {};
      scale = base.scale ?? 1;
      ox = base.offsetX ?? 0;
      oy = base.offsetY ?? 0;

      const animType = slide.animationType;
      const timing = slide.animationTiming;
      const animDuration = slide.animationDuration;

      let applyAnim = false;
      let p = pFull;

      if (animType && ANIMATION_DELTAS[animType]) {
        const w = animDuration / slide.slideDuration;

        if (timing === "start" && pFull <= w) {
          applyAnim = true;
          p = pFull / w;
        } else if (timing === "end" && pFull >= 1 - w) {
          applyAnim = true;
          p = (pFull - (1 - w)) / w;
        } else if (timing === "both") {
          if (pFull <= w) {
            applyAnim = true;
            p = pFull / w;
          } else if (pFull >= 1 - w) {
            applyAnim = true;
            p = (pFull - (1 - w)) / w;
          }
        } else {
          applyAnim = true;
          p = pFull;
        }
      }

      if (applyAnim) {
        const delta = ANIMATION_DELTAS[animType](
          Math.max(0, Math.min(1, p)),
          canvas
        );

        scale *= delta.scale ?? 1;
        ox += delta.offsetX ?? 0;
        oy += delta.offsetY ?? 0;

        // 🔸 Visual-only overlays (flash, shake, etc.)
        if (animType === "flash-pop") {
          const flash = Math.sin(p * Math.PI) * 0.8;
          if (flash > 0.05) {
            ctx.fillStyle = `rgba(255,255,255,${flash})`;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
        }
      }
    }

    // ✅ FINAL DRAW
    // 🎨 APPLY FILTER
    const filterType = filters?.[index] || "none";
    ctx.filter = getCanvasFilter(filterType);
    const bounds = drawImageFit(ctx, imgA, canvas, scale, ox, oy);
    // 🔴 RESET FILTER
    ctx.filter = "none";
    boundsRef.current = { ...bounds, index };
    // 🔐 CAPTURE FINAL TRANSFORM (used for export)
    if (!exportTransformsRef.current[index]) {
      exportTransformsRef.current[index] = {};
    }

    exportTransformsRef.current[index] = {
  scale:
    (bounds.drawW / canvas.width) * 1080 / imgA.width,

  offsetX:
    bounds.dx + bounds.drawW / 2 - canvas.width / 2,

  offsetY:
    bounds.dy + bounds.drawH / 2 - canvas.height / 2,

  canvasWidth: canvas.width,
  canvasHeight: canvas.height
};


    if (selectedIndexRef.current === index) {
      drawHandles(ctx, boundsRef.current);
    }

    return;
  }

  // -----------------------------
  // ---------- TRANSITION --------
  // -----------------------------
  const p = Math.min(
    1,
    Math.max(
      0,
      (local - slide.slideDuration) / transitionDuration
    )
  );

  const transitionType = slide.transitionType;
  const transitionFn =
    TRANSITIONS[transitionType] || TRANSITIONS.fade;

  // A transform (live > keyframes > static)
  let scaleA, oxA, oyA;

  if (draggingRef.current?.index === index) {
    scaleA = live.scale ?? 1;
    oxA = live.offsetX ?? 0;
    oyA = live.offsetY ?? 0;
  } else if (hasKeyframes) {
    const kf = getInterpolatedTransform(index, local);
    scaleA = kf.scale ?? 1;
    oxA = kf.offsetX ?? 0;
    oyA = kf.offsetY ?? 0;
  } else {
    const base = slideTransforms[index] || {};
    scaleA = base.scale ?? 1;
    oxA = base.offsetX ?? 0;
    oyA = base.offsetY ?? 0;
  }

  // B transform (keyframes only, no animations)
  const nextKF = getInterpolatedTransform(nextIndex, 0);
  const scaleB = nextKF.scale ?? 1;
  const oxB = nextKF.offsetX ?? 0;
  const oyB = nextKF.offsetY ?? 0;
  const baseIndex = slide.index;
  const transitionIndex = (baseIndex + 1) % loaded.length;

  const filterA = filters?.[baseIndex] || "none";
  const filterB = filters?.[transitionIndex] || "none";


  transitionFn(
    ctx,
    imgA,
    imgB,
    p,
    canvas,
    scaleA,
    scaleB,
    oxA,
    oyA,
    oxB,
    oyB,
    filterA,
    filterB
  );

  if (
    boundsRef.current &&
    selectedIndexRef.current === boundsRef.current.index
  ) {
    drawHandles(ctx, boundsRef.current);
  }
};




const lastTimestampRef = useRef(0);
const internalTimeRef = useRef(0); // master clock for NO-AUDIO mode


// Animation loop
const loop = (timestamp) => {
  if (!playingRef.current) return;
  if (!loaded.length || totalDuration === 0) return;

  let next = 0;

  // ---------- CASE 1: NO AUDIO ----------
  if (audioTracks.length === 0) {
    if (!lastTimestampRef.current) lastTimestampRef.current = timestamp;

    const delta = (timestamp - lastTimestampRef.current) / 1000;
    lastTimestampRef.current = timestamp;

    internalTimeRef.current += delta;
    next = internalTimeRef.current;

    if (next >= totalDuration) {
      next = 0;
      internalTimeRef.current = 0;
    }
  }

  // ---------- CASE 2: WITH AUDIO ----------
  else {
    next = audioEngine.getCurrentTime();

    // ---- MAIN LOOP HANDLING (was missing!) ----
    if (next >= totalDuration) {
      audioEngine.seek(0);      // reset audio
      internalTimeRef.current = 0;
      next = 0;
    }

    // ---- FALLBACK TIMING ----
    if (!next || isNaN(next)) {
      if (!lastTimestampRef.current) lastTimestampRef.current = timestamp;

      const delta = (timestamp - lastTimestampRef.current) / 1000;
      lastTimestampRef.current = timestamp;

      internalTimeRef.current += delta;
      next = internalTimeRef.current;

      if (next >= totalDuration) {
        audioEngine.seek(0);
        next = 0;
        internalTimeRef.current = 0;
      }
    }
  }

  drawFrame(next);
  setCurrentTime(next);

  rafRef.current = requestAnimationFrame(loop);
};






// Play/Pause toggle
const togglePlay = () => {
  if (!playing) {
    playingRef.current = true;
    setPlaying(true);

    lastTimestampRef.current = 0;
    internalTimeRef.current = currentTime; // <-- start from exact seek position

    if (audioTracks.length > 0) {
      audioEngine.play(currentTime);
    }

    rafRef.current = requestAnimationFrame(loop);
  } else {
    playingRef.current = false;
    setPlaying(false);
    redraw();
    cancelAnimationFrame(rafRef.current);

    if (audioTracks.length > 0) {
      audioEngine.pause();
    }
  }
};





// Seek handler
const handleSeek = (t) => {
  setCurrentTime(t);
  internalTimeRef.current = t;     // <-- critical fix
  drawFrame(t);

  if (audioTracks.length > 0) {
    audioEngine.seek(t);
  }

  lastTimestampRef.current = 0;
};



useEffect(() => {
  playingRef.current = false;
  setPlaying(false);

  cancelAnimationFrame(rafRef.current);
  audioEngine.stopAll();

  setCurrentTime(0);

  if (loaded.length) drawFrame(0);
}, [images, durations, transitionDuration,audioTracks]);

// Keyboard shortcut: K to add keyframe
useEffect(() => {
  function onKeyDown(e) {
    // ✅ Press K to add a keyframe
    if (e.key.toLowerCase() === "k") {
      e.preventDefault();
      addKeyframeForCurrentSlide();
    }
  }

  window.addEventListener("keydown", onKeyDown);
  return () => window.removeEventListener("keydown", onKeyDown);
}, [currentIndex, currentTime, timeline, slideTransforms]);

const globalKeyframes = useMemo(() => {
  const map = [];
  timeline.forEach((slide) => {
    const list = keyframes?.[slide.index] || [];
    list.forEach(kf => {
      map.push({
        slideIndex: slide.index,
        time: slide.start + kf.time
      });
    });
  });
  return map;
}, [timeline, keyframes]);




  return (
    <div className="flex flex-col gap-4 items-center w-full 
      p-4 rounded-lg
      bg-gray-100 dark:bg-gray-900
      border border-gray-200 dark:border-gray-800
      transition-colors
    ">
      <canvas
        ref={canvasRef}
        width={canvasSize.width}
        height={canvasSize.height}   // ✅ exact 9:16
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        className="
          rounded
          bg-gray-300 dark:bg-black
          border border-gray-300 dark:border-gray-800
          transition-colors
        "
      />
      <button
        className="
                p-2 rounded-md
                bg-gray-300 hover:bg-gray-300
                dark:bg-gray-800 dark:hover:bg-gray-700
                border border-gray-300 dark:border-gray-700
                text-gray-900 dark:text-gray-100
                transition-colors
              "
        onClick={() => fitSlideVertically(currentIndex)}
        style={{
          position: "relative",
          top: 12,
          right: 12,
          padding: "6px 10px",
          cursor: "pointer",
        }}
        >
        Fit Vertical
      </button>
      

      {/* Controls */}
      <div className="flex items-center justify-between gap-4">
        {/* Left: Play / Time */}
        <div className="flex items-center gap-4">
          <button
            onClick={togglePlay}
            className="
              p-2 rounded-md
              bg-gray-300 hover:bg-gray-300
              dark:bg-gray-800 dark:hover:bg-gray-700
              border border-gray-300 dark:border-gray-700
              text-gray-900 dark:text-gray-100
              transition-colors
            "
          >
            {playing ? <Pause size={20} /> : <Play size={20} />}
          </button>

          <div className="text-sm text-gray-900 dark:text-gray-200">
            {currentTime.toFixed(2)} / {totalDuration.toFixed(2)}
          </div>
        </div>

        {/* Right: Add Keyframe */}
        <button
          onClick={addKeyframeForCurrentSlide}
          className="
            px-3 py-1 rounded-md
            bg-gray-300 hover:bg-gray-500
            text-gray-900 hover:text-white
            transition-colors
          "
        >
          + Keyframe
        </button>
      </div>



      <TimelineBar
        duration={totalDuration}
        current={currentTime}
        onSeek={handleSeek}
        onSeekEnd={handleSeek}
        zoom={1}
        pxPerSec={80}
        keyframeMarkers={globalKeyframes}
        activeSlideIndex={currentIndex}
      />
      <TrackView
        images={loaded}
        durations={durations}
        pxPerSec={80}
        zoom={1}
        onDurationChange={(idx, newDuration) => {
          const d = [...durations];
          d[idx] = newDuration;
          setDurations(d);
        }}
        setImages={setLoaded}          // ✅ ADD
        setDurations={setDurations}    // ✅ ADD
        clipTransitions={clipTransitions}  
        setClipTransitions={setClipTransitions} 
        onSelectTransition={onSelectTransition}
        clipAnimations={clipAnimations}           // ✅ ADD
        setClipAnimations={setClipAnimations}     // ✅ ADD
        clipAnimationTiming={clipAnimationTiming}           // ✅ ADD
        setClipAnimationTiming={setClipAnimationTiming}     // ✅ ADD
        animationDuration={animationDuration}           // ✅ ADD
        onSelectAnimation={onSelectAnimation}
      />
    </div>
  );
}

